import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useSearchParams } from 'react-router-dom'
import { Plus, ClipboardList } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { CreateAssessmentForm } from '@/features/assessments/forms/create-assessment-form'
import { AssessmentCard } from '@/features/assessments/components/assessment-card'
import { ImportExportButtons } from '@/components/ui/import-export-buttons'
import { useAssessments, useDeleteAssessment } from '@/features/assessments/hooks'
import { useOrganizations } from '@/features/organizations/hooks'
import { useListState } from '@/hooks/use-list-state'
import { DataToolbar } from '@/components/ui/data-toolbar'
import { DataPagination } from '@/components/ui/data-pagination'
import { AuthorizationGate } from '@/features/authorization/AuthorizationGate'
import { useToast } from '@/hooks/use-toast'
import type { Assessment } from '@/features/assessments/types'
import type { PagedRequest } from '@/shared/types'

// ── Organization Multiselect Filter ──────────────────────────────────────────

interface OrgOption { id: string; name: string }

function OrganizationFilter({
    selected,
    onChange,
}: {
    selected: string[]
    onChange: (ids: string[]) => void
}) {
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')

    const { data: orgData, isLoading } = useOrganizations({ per_page: 200, sort_by: 'name', sort_order: 'asc' })
    const organizations: OrgOption[] = (orgData?.items ?? []).map((o: any) => ({ id: o.id, name: o.name }))

    const filtered = search
        ? organizations.filter(o => o.name.toLowerCase().includes(search.toLowerCase()))
        : organizations

    const toggle = (id: string) => {
        onChange(selected.includes(id) ? selected.filter(v => v !== id) : [...selected, id])
    }

    // Close on outside click
    useEffect(() => {
        if (!open) return
        const handler = (e: MouseEvent) => {
            const target = e.target as HTMLElement
            if (!target.closest('[data-org-filter]')) setOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [open])

    const selectedNames = organizations.filter(o => selected.includes(o.id)).map(o => o.name)

    return (
        <div className="relative" data-org-filter>
            <button
                onClick={() => setOpen(!open)}
                className="inline-flex items-center justify-between whitespace-nowrap rounded-md border border-input bg-background px-3 py-2 text-sm h-9 min-w-[160px] hover:bg-accent/50 transition-colors"
            >
                <span className="flex items-center gap-1.5 truncate">
                    {selected.length > 0 ? (
                        <>
                            <span className="truncate max-w-[120px]">
                                {selectedNames.length === 1 ? selectedNames[0] : `${selectedNames.length} orgs`}
                            </span>
                            <span className="ml-1 h-5 px-1.5 text-[10px] font-medium bg-secondary text-secondary-foreground rounded-full inline-flex items-center">
                                {selected.length}
                            </span>
                        </>
                    ) : (
                        <span className="text-muted-foreground">Organization</span>
                    )}
                </span>
                <svg className={`ml-2 h-3.5 w-3.5 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
            </button>

            {open && (
                <div className="absolute z-50 mt-1 w-64 rounded-md border bg-popover shadow-md animate-in fade-in-0 zoom-in-95">
                    {/* Search */}
                    <div className="p-2 border-b">
                        <input
                            type="text"
                            placeholder="Search organizations..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="w-full rounded-sm border border-input px-2 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                    </div>

                    {/* List */}
                    <div className="max-h-60 overflow-y-auto p-1">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-4">
                                <LoadingSpinner />
                            </div>
                        ) : filtered.length > 0 ? (
                            filtered.map(org => (
                                <label
                                    key={org.id}
                                    className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer transition-colors"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(org.id)}
                                        onChange={() => toggle(org.id)}
                                        className="rounded border-gray-300"
                                    />
                                    <span className="truncate">{org.name}</span>
                                </label>
                            ))
                        ) : (
                            <p className="px-2 py-3 text-sm text-muted-foreground text-center">No organizations found</p>
                        )}
                    </div>

                    {/* Clear */}
                    {selected.length > 0 && (
                        <button
                            className="w-full text-xs text-muted-foreground hover:text-foreground py-1.5 border-t transition-colors"
                            onClick={() => { onChange([]); setOpen(false) }}
                        >
                            Clear filter
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

// ── Assessments Page ─────────────────────────────────────────────────────────

export function AssessmentsPage() {
    const [showCreateForm, setShowCreateForm] = useState(false)
    const [assessmentToDelete, setAssessmentToDelete] = useState<Assessment | null>(null)
    const { toast } = useToast()

    // ── URL-persisted organization filter ────────────────────────────────
    const [searchParams, setSearchParams] = useSearchParams()
    const orgFilterFromUrl = searchParams.get('organization_id')?.split(',').filter(Boolean) ?? []
    const [selectedOrgs, setSelectedOrgs] = useState<string[]>(orgFilterFromUrl)

    // Sync selectedOrgs → URL params
    const handleOrgFilterChange = (ids: string[]) => {
        setSelectedOrgs(ids)
        setSearchParams(prev => {
            const next = new URLSearchParams(prev)
            if (ids.length > 0) {
                next.set('organization_id', ids.join(','))
            } else {
                next.delete('organization_id')
            }
            return next
        }, { replace: true })
        // Reset to page 1 when filter changes
        listState.setPage(1)
    }

    const listState = useListState({ defaultPerPage: 6, defaultSortBy: 'created_at' })

    // Build query params with org filter
    const queryParams: PagedRequest = {
        ...listState.queryParams,
        filters: {
            ...listState.queryParams.filters,
            ...(selectedOrgs.length > 0 ? { organization_id: selectedOrgs } : {}),
        },
    }

    const { data: assessmentResponse, isLoading } = useAssessments(queryParams)
    const deleteMutation = useDeleteAssessment()

    const handleDelete = (assessment: Assessment) => {
        deleteMutation.mutate(assessment.id, {
            onSuccess: () => {
                setAssessmentToDelete(null)
                toast({ title: 'Assessment deleted', description: 'Assessment has been deleted successfully.' })
            },
            onError: (error: any) => {
                toast({
                    title: 'Error',
                    description: error.response?.data?.error || 'Failed to delete assessment',
                    variant: 'destructive',
                })
            },
        })
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="lg" />
            </div>
        )
    }

    const assessments: Assessment[] = assessmentResponse?.items ?? []
    const totalItems = assessmentResponse?.total ?? 0
    const totalPages = assessmentResponse?.pages ?? 1

    return (
        <motion.div
            className="space-y-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
        >
            {/* Header */}
            <div className="md:flex md:items-center md:justify-between">
                <div className="min-w-0 flex-1">
                    <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
                        Assessments
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                        Manage all IT maturity assessments across your organizations
                    </p>
                </div>
                <div className="mt-4 flex items-center gap-3 md:ml-4 md:mt-0">
                    <ImportExportButtons
                        entityType="assessments"
                        entityLabel="Assessments"
                    />
                    <AuthorizationGate actions={['create_assessment']}>
                        <Button onClick={() => setShowCreateForm(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            New Assessment
                        </Button>
                    </AuthorizationGate>
                </div>
            </div>

            {/* Toolbar */}
            <DataToolbar
                listState={listState}
                searchPlaceholder="Search assessments..."
                sortOptions={[
                    { label: 'Name', value: 'name' },
                    { label: 'Created', value: 'created_at' },
                    { label: 'Status', value: 'status' },
                ]}
                filters={[
                    {
                        key: 'status',
                        label: 'Status',
                        options: [
                            { label: 'Draft', value: 'draft' },
                            { label: 'In Progress', value: 'in_progress' },
                            { label: 'Completed', value: 'completed' },
                        ],
                    },
                ]}
                totalItems={totalItems}
                perPageOptions={[6, 12, 24]}
                extraFilters={
                    <OrganizationFilter
                        selected={selectedOrgs}
                        onChange={handleOrgFilterChange}
                    />
                }
            />

            {/* Assessments List (full-width rows) */}
            {assessments.length > 0 ? (
                <>
                    <div className="space-y-3">
                        {assessments.map((assessment: Assessment, i: number) => (
                            <AssessmentCard
                                key={assessment.id}
                                assessment={assessment}
                                index={i}
                                onDelete={setAssessmentToDelete}
                            />
                        ))}
                    </div>

                    {/* Pagination */}
                    <DataPagination
                        page={listState.page}
                        totalPages={totalPages}
                        onPageChange={listState.setPage}
                        totalItems={totalItems}
                        perPage={listState.perPage}
                    />
                </>
            ) : (
                <div className="text-center py-12">
                    <ClipboardList className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                        {listState.search || selectedOrgs.length > 0 ? 'No matching assessments' : 'No assessments'}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                        {listState.search || selectedOrgs.length > 0
                            ? 'Try adjusting your search or filters.'
                            : 'Get started by creating your first assessment.'}
                    </p>
                    {!listState.search && selectedOrgs.length === 0 && (
                        <AuthorizationGate actions={['create_assessment']}>
                            <div className="mt-6">
                                <Button onClick={() => setShowCreateForm(true)}>
                                    <Plus className="mr-2 h-4 w-4" />
                                    New Assessment
                                </Button>
                            </div>
                        </AuthorizationGate>
                    )}
                </div>
            )}

            {/* Create Assessment Form */}
            {showCreateForm && (
                <CreateAssessmentForm
                    onClose={() => setShowCreateForm(false)}
                    onSuccess={() => {
                        setShowCreateForm(false)
                    }}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <AuthorizationGate actions={['delete_assessment']}>
                <Dialog open={!!assessmentToDelete} onOpenChange={(open) => { if (!open) setAssessmentToDelete(null) }}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Delete Assessment</DialogTitle>
                            <DialogDescription>
                                Are you sure you want to delete &quot;{assessmentToDelete?.name}&quot;? This will also delete all associated answers and audit trail entries. This action cannot be undone.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setAssessmentToDelete(null)}>
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={() => assessmentToDelete && handleDelete(assessmentToDelete)}
                                disabled={deleteMutation.isPending}
                            >
                                {deleteMutation.isPending ? 'Deleting...' : 'Delete Assessment'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </AuthorizationGate>
        </motion.div>
    )
}
